import React, { Suspense } from 'react';
import tw from 'twin.macro';
import styled from '@emotion/styled';
import { css, SerializedStyles } from '@emotion/react';

import { withProperties } from '$/utils/type';

import Form from './Form';
import SideBar, { SideBarProps } from '../SideBar/SideBar';

const Settings = {};

interface SectionProps {
  id: string;
  children: React.ReactNode;
  className?: string;
  limitWidth?: string;
}

const Separator = styled.div`
  ${tw`border-t border-gray-300 mt-2e pt-5 w-full`}
`;

const ToggleSettings = styled.div`
  padding-top: 0.75rem;

  & > .setting {
    margin-top: 0.75rem !important;
  }
  & > .setting:not(.hidden):last-child {
    margin-bottom: 0.75rem;
  }
`;

const Toggle = styled(
  ({
    show: _show,
    ...props
  }: React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> & {
    show?: boolean;
  }) => <div {...props} className={`${props.className} setting toggle`} />
)`
  flex-direction: column;
  align-items: flex-start !important;
  & > ${ToggleSettings} {
    position: relative;
    ${(props): string => (props.show ? '' : 'display: none;')}

    &:not(.no-indent) {
      left: 3.5rem;
      width: calc(100% - 3.5rem) !important;
    }

    &.no-indent {
      left: 0;
    }

    &:not(.no-indent) ${ToggleSettings} {
      left: 0;
      width: inherit;
    }
  }
  & > ${Form.Field}:first-child, & > .FormField:first-child {
    margin-top: 0;
  }
`;

const Setting = styled(
  (props: React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>) => (
    <div {...props} className={`${props.className} setting`} />
  )
)`
  & > ${Form.Field}:first-child, & > .FormField:first-child {
    margin-top: 0;
  }
`;

const Description = styled.div`
  ${tw`text-md font-normal leading-relaxed text-gray-1`}
  margin-top: 0.75rem;
  ul {
    list-style: disc;
    margin-left: 2rem;
  }
  ol {
    list-style: decimal;
    margin-left: 2rem;
  }
  a {
    text-decoration: underline;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const SectionTitle = styled.h2`
  ${tw`inline-flex w-full`}
  ${tw`font-title text-title font-medium leading-1_75e`}
  margin-bottom: 0;
`;

const Title = styled.header`
  ${tw`inline-flex w-full items-center font-normal leading-none text-2xl`}
  margin-bottom: 0;
`;

const SectionCss = tw`mb-1_5e border rounded-xl border-color-border bg-white`;

const Section: React.FC<SectionProps> = ({ id, children, className, limitWidth = '25rem' }) => {
  return (
    <section
      id={id}
      className={className}
      css={[
        SectionCss,
        css`
          width: 100%;

          .error-message {
            min-width: 17rem !important;
          }
          .input,
          .input + .error-message {
            max-width: ${limitWidth};
          }
          & > ${Separator} {
            padding: 0;
            margin-top: 0;
            margin-bottom: 0;
          }
          & ${Form.Field}, & .FormField {
            margin-top: 0.75rem;
            margin-bottom: 0;
          }
          & > .subsection:first-of-type > ${Title}:first-of-type {
            ${tw`font-title text-title font-medium leading-1_75e`}
          }
          button ${Form.Field}, button .FormField {
            margin-top: 0;
          }

          .radio + .setting.toggle {
            margin-top: 0;
          }
        `,
      ]}
    >
      {children}
    </section>
  );
};

const SubsectionCss = tw`flex flex-col w-full p-1_5e`;

const Subsection: React.FC<{
  id?: string;
  children: React.ReactNode;
  className?: string;
  offsetCss?: SerializedStyles;
}> = ({ id, children, className, offsetCss }) => {
  return (
    <div
      id={id}
      className={`subsection ${className}`}
      css={[
        SubsectionCss,
        offsetCss,
        css`
          & > ${Setting}:first-child, & > ${Toggle}:first-child {
            margin-top: 0;
          }
          .setting {
            ${tw`flex items-center text-md font-normal text-color-setting`}
            margin-top: 2rem;
            & .ui.checkbox {
              label {
                ${tw`text-md`}
              }
            }
            &:last-child {
              margin-bottom: 0;
            }
            &.toggle + .setting.toggle {
              margin-top: 1.25rem;
            }
            & ${Form.Field}, & .FormField {
              margin-bottom: 0;
            }
          }
        `,
      ]}
    >
      {children}
    </div>
  );
};

const Label = styled.div`
  ${tw`flex items-center text-lg font-normal text-color-setting`}
`;

const Subtitle = styled.header`
  ${tw`flex items-center text-xl font-normal mb-2e`}
`;

const Page: React.FC<
  SideBarProps & {
    width?: 'wide' | 'normal';
    noPadding?: boolean;
    basic?: boolean;
    hasSections?: boolean;
  }
> = ({
  title,
  width,
  page,
  hasSections,
  excludeSections,
  dynamicSectionsFn,
  className,
  noPadding,
  basic,
  children,
}) => {
  if (width) {
    className =
      (width === 'normal' ? 'page mx-auto h-full ' : 'wide-page mx-auto h-full ') +
      (className ?? '');
  }

  if (!page) {
    if (hasSections || basic) {
      return (
        <div className={`Page ${className} overflow-hidden rounded-xl border-color-border `}>
          {children}
        </div>
      );
    }

    return (
      <div className={`Page ${className} overflow-hidden`} css={SectionCss}>
        <div className={`PageSubsection h-full ${noPadding && 'p-0'}`} css={SubsectionCss}>
          {children}
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full flex ${className}`}>
      <Suspense>
        <SideBar
          title={title}
          page={page}
          excludeSections={excludeSections}
          dynamicSectionsFn={dynamicSectionsFn}
        />
      </Suspense>
      <div
        className='flex flex-col relative'
        css={css`
          width: 100%;
          @media only screen and (min-width: 992px) {
            width: calc(100% - 20rem);
          }
          section {
            max-width: 100%;
            &:last-child {
              margin-bottom: calc(100vh - 235px) !important;
            }
          }
          ${Form.Field}, .FormField {
            width: 100%;
          }
        `}
      >
        {children}
      </div>
    </div>
  );
};

export default withProperties(Settings, {
  Page,
  Section,
  SectionTitle,
  Subsection,
  Title,
  Subtitle,
  Separator,
  Setting,
  Label,
  Description,
  Toggle,
  ToggleSettings,
});
